import React, { useEffect, useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useLanguage } from 'lib/i18n';
import api, { getApiErrorMessage } from 'lib/api';
import { formatDateTime, formatPrice } from 'lib/format';
import { toast } from 'sonner';
import { BadgePercent, Plus, RefreshCw, Ban, TicketPercent } from 'lucide-react';

const initialForm = { code: '', discount_type: 'percent', discount_value: '', ends_at: '', max_uses: '' };

export default function AdminDiscountCodesPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deactivating, setDeactivating] = useState('');

  const loadCodes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/discount-codes');
      setCodes(response.data || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('discountAdmin.loadError')));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadCodes(); }, []);

  const set = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const createCode = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/discount-codes', {
        ...form,
        code: form.code.trim().toUpperCase(),
        discount_value: Number(form.discount_value),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
      });
      toast.success(t('discountAdmin.created'));
      setForm(initialForm);
      await loadCodes();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('discountAdmin.createError')));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (code) => {
    if (!window.confirm(t('discountAdmin.deactivateConfirm').replace('{code}', code.code))) return;
    setDeactivating(code.id);
    try {
      await api.post(`/admin/discount-codes/${code.id}/deactivate`);
      toast.success(t('discountAdmin.deactivated'));
      await loadCodes();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('discountAdmin.deactivateError')));
    } finally {
      setDeactivating('');
    }
  };

  const describeDiscount = (code) => code.discount_type === 'percent'
    ? `${Number(code.discount_value)}%`
    : formatPrice(code.discount_value);

  return (
    <DashboardShell>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><BadgePercent size={21} /></div>
        <div>
          <h1 className="text-2xl font-bold">{t('discountAdmin.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('discountAdmin.description')}</p>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(320px,0.7fr)_minmax(0,1.3fr)]">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Plus size={17} />{t('discountAdmin.createTitle')}</CardTitle></CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={createCode}>
              <div className="space-y-2">
                <Label htmlFor="discount-code">{t('discountAdmin.code')}</Label>
                <Input id="discount-code" required minLength={3} maxLength={32} pattern="[A-Za-z0-9_-]+" value={form.code} onChange={event => set('code', event.target.value.toUpperCase())} placeholder="BIENVENUE10" data-testid="discount-code-input" />
                <p className="text-xs text-muted-foreground">{t('discountAdmin.codeHelp')}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>{t('discountAdmin.kind')}</Label>
                  <Select value={form.discount_type} onValueChange={value => set('discount_type', value)}>
                    <SelectTrigger data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">{t('discountAdmin.percent')}</SelectItem>
                      <SelectItem value="fixed">{t('discountAdmin.fixed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="discount-value">{form.discount_type === 'percent' ? t('discountAdmin.percentValue') : t('discountAdmin.fixedValue')}</Label>
                  <Input id="discount-value" type="number" required min="0.01" max={form.discount_type === 'percent' ? 99.99 : undefined} step="0.01" value={form.discount_value} onChange={event => set('discount_value', event.target.value)} data-testid="discount-value-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount-end">{t('discountAdmin.endsAt')}</Label>
                <Input id="discount-end" type="datetime-local" value={form.ends_at} onChange={event => set('ends_at', event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount-max-uses">{t('discountAdmin.maxUses')}</Label>
                <Input id="discount-max-uses" type="number" min="1" step="1" value={form.max_uses} onChange={event => set('max_uses', event.target.value)} placeholder={t('discountAdmin.unlimited')} />
              </div>
              <Button type="submit" disabled={saving} className="w-full" data-testid="discount-create-button">
                <TicketPercent size={15} className="mr-2" />{saving ? t('discountAdmin.creating') : t('discountAdmin.create')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>{t('discountAdmin.codes')}</CardTitle>
            <Button variant="ghost" size="icon" aria-label={t('discountAdmin.refresh')} onClick={loadCodes} disabled={loading}><RefreshCw size={16} /></Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {!codes.length && <p className="text-sm text-muted-foreground">{loading ? t('discountAdmin.loading') : t('discountAdmin.empty')}</p>}
            {codes.map(code => {
              const expired = code.ends_at && new Date(code.ends_at).getTime() <= Date.now();
              const exhausted = code.max_uses != null && Number(code.usage_count || 0) + Number(code.reserved_count || 0) >= Number(code.max_uses);
              const active = code.active && !expired && !exhausted;
              return (
                <div key={code.id} className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="discount-code-row">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-base font-bold tracking-wide">{code.code}</span>
                      <span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">{describeDiscount(code)}</span>
                      <span className={`rounded-full px-2 py-1 text-xs ${active ? 'bg-emerald-100 text-emerald-800' : 'bg-muted text-muted-foreground'}`}>
                        {active ? t('discountAdmin.active') : expired ? t('discountAdmin.expired') : exhausted ? t('discountAdmin.exhausted') : t('discountAdmin.inactive')}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{t('discountAdmin.usage')}: {code.usage_count || 0}{code.max_uses ? ` / ${code.max_uses}` : ` · ${t('discountAdmin.unlimited')}`}</span>
                      {Number(code.reserved_count || 0) > 0 && <span>{t('discountAdmin.reserved')}: {code.reserved_count}</span>}
                      <span>{code.ends_at ? t('discountAdmin.ends').replace('{date}', formatDateTime(code.ends_at)) : t('discountAdmin.noEnd')}</span>
                    </div>
                  </div>
                  {code.active && <Button size="sm" variant="outline" disabled={deactivating === code.id} onClick={() => deactivate(code)}>
                    <Ban size={14} className="mr-1.5" />{deactivating === code.id ? t('discountAdmin.deactivating') : t('discountAdmin.deactivate')}
                  </Button>}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  );
}
